import { POLL_MS } from '../lib/polling';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { sortCategories, VOID_REASONS } from '@kortex/shared';
import { useAuth } from '../lib/auth';
import { usePosDataSource } from '../lib/posData/usePosData';
import { estimateBill } from '../lib/posData/estimate';
import type { PosDataSource, StayDto, TableDto, TicketLineDto } from '../lib/posData/types';
import { PosStatusChip } from '../components/PosStatusChip';
import { ConnectivityIndicator } from '../components/ConnectivityIndicator';
import { SyncActivityIndicator } from '../components/SyncActivityIndicator';
import { money, money0, pct, time } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { TableSetup } from '../components/TableSetup';
import { TableQrCodes } from '../components/TableQrCodes';
import { InvoiceDoc, type Invoice } from '../components/InvoiceDoc';
import { PaperWidthPicker, ThermalPrintButton } from '../components/ThermalPrint';
import { invoiceReceipt, ticketKot } from '../lib/receipt';
import { Empty, ErrorState, Loading, Modal, Select, TextInput, useToast } from '../components/ui';
import { ScrollStrip } from '../components/ScrollStrip';
import { RecordExpenseModal } from './Expenses';
import { useBarcodeScan } from '../lib/useBarcodeScan';
import { useBackToClose } from '../lib/useBackToClose';
import {
  clearCustomerDisplay,
  isAndroidKiosk,
  showCustomerDisplay,
} from '../lib/androidBridge';

const PAYMENT_MODES = ['CASH', 'CARD', 'UPI', 'ROOM'] as const;

/** Anything the list does not cover, written out in full. */
const OTHER = '__other__';

export function PosPage() {
  const queryClient = useQueryClient();
  const { push, fail } = useToast();
  const { can, session } = useAuth();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  // A percentage, not a rupee amount: the counter thinks in percent, and a
  // percentage stays right when a line is added after it was typed.
  const [discountPct, setDiscountPct] = useState(0);
  const [mode, setMode] = useState<(typeof PAYMENT_MODES)[number]>('CASH');
  const [newTicket, setNewTicket] = useState(false);
  const [tableSetup, setTableSetup] = useState(false);
  const [qrCodes, setQrCodes] = useState(false);
  const [recordingExpense, setRecordingExpense] = useState(false);
  const [voidTicket, setVoidTicket] = useState(false);
  const [removing, setRemoving] = useState<TicketLineDto | null>(null);
  const [receipt, setReceipt] = useState<Invoice | null>(null);
  /**
   * The cart as a full-screen sheet. Only has an effect below 768px, where the
   * ticket cannot sit beside the menu; wider screens always show it in place.
   */
  const [cartOpen, setCartOpen] = useState(false);

  // The Phase 3 seam: API in the browser, SQLite on the kiosk - the page
  // cannot tell (docs/offline/phase-3-local-first-implementation.md).
  const { source, isLoading: sourceLoading, error: sourceError, retry: retrySource } = usePosDataSource();

  const menu = useQuery({
    // Keyed by the filter, not just by 'menu'. The menu, offers and online
    // order screens ask the same endpoint for every item, on sale or not; with
    // one shared key the till rendered whichever list was fetched last and put
    // items nobody had put on sale in front of a cashier.
    queryKey: ['menu', { activeOnly: true }],
    queryFn: () => source!.getMenu(),
    enabled: Boolean(source),
  });
  const tickets = useQuery({
    queryKey: ['tickets', 'OPEN'],
    queryFn: () => source!.listOpenTickets(),
    refetchInterval: POLL_MS,
    enabled: Boolean(source),
  });

  const activeTicketId = activeId ?? tickets.data?.[0]?.id ?? null;
  const ticket = useQuery({
    queryKey: ['ticket', activeTicketId],
    queryFn: () => source!.getTicket(activeTicketId!),
    /*
     * The lines are on the ticket, so the kitchen state each one shows is only
     * as current as this. Without a poll at all it would show whatever was
     * true when the ticket was opened, which is worse than showing nothing -
     * a status display gets believed. Adding or removing an item refetches
     * immediately, so in practice it is usually fresher than the interval.
     */
    refetchInterval: POLL_MS,
    enabled: Boolean(source && activeTicketId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    void queryClient.invalidateQueries({ queryKey: ['ticket', activeTicketId] });
  };

  const addLine = useMutation({
    mutationFn: (input: { menuItemId: string; quantity: number }) =>
      source!.addLine(activeTicketId!, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['ticket', activeTicketId], data);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: fail,
  });

  const removeLine = useMutation({
    mutationFn: (lineId: string) => source!.removeLine(activeTicketId!, lineId),
    onSuccess: (data) => {
      queryClient.setQueryData(['ticket', activeTicketId], data);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError: fail,
  });

  const bill = useMutation({
    mutationFn: () =>
      source!.billTicket(activeTicketId!, {
        mode,
        discountPct,
        discount,
        discountReason: discountPct > 0 ? `Counter discount ${discountPct}%` : '',
      }),
    onSuccess: (invoice) => {
      push(`${invoice.number} settled · ${money0(invoice.total)}`, 'ok');
      setReceipt(invoice);
      setDiscountPct(0);
      setActiveId(null);
      // Back to the menu: the next ticket is a new start, not this sheet again.
      setCartOpen(false);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: fail,
  });

  const voidMutation = useMutation({
    mutationFn: (reason: string) => source!.voidTicket(activeTicketId!, reason),
    onSuccess: () => {
      push('Ticket voided');
      setVoidTicket(false);
      setActiveId(null);
      setCartOpen(false);
      invalidate();
    },
    onError: fail,
  });

  // Menu-card order, the same on every screen: a cashier who learns where
  // starters sit here finds them in the same place on the menu screen.
  const categories = useMemo(
    () => ['All', ...sortCategories([...new Set((menu.data ?? []).map((item) => item.category))])],
    [menu.data],
  );

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (menu.data ?? []).filter(
      (item) =>
        (category === 'All' || item.category === category) &&
        (term === '' ||
          item.name.toLowerCase().includes(term) ||
          item.sku.toLowerCase().includes(term)),
    );
  }, [menu.data, category, search]);

  const current = ticket.data;
  // The server's subtotal already carries any packing or delivery charge, and a
  // discount is on the meal rather than on the box it leaves in - the
  // percentage is applied against the food-only portion, then handed to the
  // same shared GST engine the settle runs, so the preview equals the final
  // bill to the paisa in both data modes (lib/posData/estimate.ts).
  const chargeTotal = current?.totals.charges ?? 0;
  const foodNet = Math.max(0, (current?.totals.subtotal ?? 0) - chargeTotal);
  const discount = Math.round((foodNet * Math.min(discountPct, 100)) / 100);
  const estimate = estimateBill(current, discount);
  const itemCount = (current?.lines ?? []).reduce((sum, line) => sum + line.quantity, 0);

  useBackToClose(cartOpen, () => setCartOpen(false));

  // Rotating a phone to landscape, or opening a tablet wide, puts the cart
  // beside the menu; a sheet left "open" would hold a history entry for a
  // layer nobody can see.
  useEffect(() => {
    const wide = window.matchMedia('(min-width: 768px)');
    const onChange = () => {
      if (wide.matches) setCartOpen(false);
    };
    wide.addEventListener('change', onChange);
    return () => wide.removeEventListener('change', onChange);
  }, []);

  // Phase 6 keyboard-wedge barcode scanning (docs/android/PHASE-6-
  // BASELINE-AUDIT.md SS4): no dedicated barcode field exists yet, so a
  // scanned code is matched against the same `sku` the search box already
  // filters by. Disabled while any modal with its own text inputs is open,
  // so a scan can never land on a ticket hidden behind one.
  const scanningEnabled =
    Boolean(source) &&
    !newTicket &&
    !tableSetup &&
    !qrCodes &&
    !voidTicket &&
    !removing &&
    !receipt &&
    !recordingExpense;
  useBarcodeScan(
    (code) => {
      setSearch('');
      if (!activeTicketId) {
        push('Start or select a ticket before scanning', 'err');
        return;
      }
      const normalized = code.trim().toLowerCase();
      const item = (menu.data ?? []).find((candidate) => candidate.sku.toLowerCase() === normalized);
      if (!item) {
        push(`No item matches scanned code "${code}"`, 'err');
        return;
      }
      addLine.mutate({ menuItemId: item.id, quantity: 1 });
      push(`Scanned ${item.name}`, 'ok');
    },
    { enabled: scanningEnabled },
  );

  // Phase 6 customer-facing display (docs/android/PHASE-6-BASELINE-AUDIT.md
  // SS2/SS22-23): mock-only, best-effort - a failure here must never block a
  // sale, so the promises are intentionally not awaited or error-handled.
  useEffect(() => {
    if (!isAndroidKiosk()) return;
    if (!current || current.lines.length === 0) {
      void clearCustomerDisplay();
      return;
    }
    void showCustomerDisplay([`Ticket ${current.number}`, money(estimate.total)]);
  }, [current, estimate.total]);

  if (sourceLoading || menu.isLoading || tickets.isLoading)
    return <Loading label="Loading the counter…" />;
  if (sourceError) return <ErrorState error={sourceError} onRetry={retrySource} />;
  if (menu.error) return <ErrorState error={menu.error} onRetry={() => void menu.refetch()} />;

  return (
    <div className="stack">
      <PageHeader
        title="POS Billing"
        subtitle="Ticket → GST invoice → automatic stock depletion"
        actions={
          <>
            {can('reservation:manage') ? (
              <>
                <button className="btn" onClick={() => setTableSetup(true)}>
                  Set up tables
                </button>
                <button className="btn" onClick={() => setQrCodes(true)}>
                  Table QR codes
                </button>
              </>
            ) : null}
            {can('expense:create') ? (
              <button className="btn" onClick={() => setRecordingExpense(true)}>
                Expense
              </button>
            ) : null}
            <button
              className="btn primary"
              data-tour="pos-new-ticket"
              onClick={() => setNewTicket(true)}
            >
              + New ticket
            </button>
          </>
        }
      />

      <div className="row between wrap gap-6">
        <ScrollStrip className="ticket-strip" label="open tickets" activeKey={activeTicketId}>
          {(tickets.data ?? []).length === 0 ? (
            <div className="chip">No open tickets — start one from the top right</div>
          ) : (
            tickets.data!.map((row) => (
              <button
                key={row.id}
                className={`ticket-tab ${row.id === activeTicketId ? 'active' : ''}`}
                onClick={() => {
                  setActiveId(row.id);
                  setDiscountPct(0);
                  setMode(row.stayId ? 'ROOM' : 'CASH');
                }}
              >
                <div className="tt-no">{row.number}</div>
                <div className="tt-meta">
                  {row.tableCode
                    ? `Table ${row.tableCode}`
                    : row.roomNumber
                      ? `Room ${row.roomNumber}`
                      : row.type.replace('_', ' ')}{' '}
                  · {money0(row.totals.subtotal)}
                </div>
              </button>
            ))
          )}
        </ScrollStrip>
        <div className="row gap-6">
          <ConnectivityIndicator />
          {source ? <SyncActivityIndicator source={source} /> : null}
          {source ? <PosStatusChip source={source} /> : null}
          <span className="chip">{tickets.data?.length ?? 0} open</span>
        </div>
      </div>

      <div className={`pos${cartOpen ? ' cart-open' : ''}`}>
        <div className="card">
          <div className="card-head">
            <ScrollStrip className="tabs" label="categories" activeKey={category}>
              {categories.map((name) => (
                <button
                  key={name}
                  className={`tab ${name === category ? 'active' : ''}`}
                  onClick={() => setCategory(name)}
                >
                  {name}
                </button>
              ))}
            </ScrollStrip>
            <div className="search pos-search">
              <input
                className="input"
                placeholder="Search item or SKU"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-scan-target=""
              />
            </div>
          </div>
          <div className="card-body">
            {visibleItems.length === 0 ? (
              <Empty message="No items match that search" />
            ) : (
              <div className="menu-grid" data-tour="pos-menu">
                {visibleItems.map((item) => (
                  <button
                    key={item.id}
                    className="menu-card"
                    disabled={!activeTicketId || addLine.isPending}
                    onClick={() => addLine.mutate({ menuItemId: item.id, quantity: 1 })}
                  >
                    <div className="row gap-6">
                      <span className={`veg-dot ${item.isVegetarian ? '' : 'nonveg'}`} />
                      <span className="xs muted mono">{item.sku}</span>
                    </div>
                    <div className="mc-name">{item.name}</div>
                    <div className="row between mc-price">
                      <span>{money0(item.price)}</span>
                      <span className="xs muted">{pct(item.foodCostPct)} FC</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card ticket">
          {!current ? (
            <Empty icon="▤" message="Select or start a ticket" />
          ) : (
            <>
              <div className="card-head">
                {/* Phone only (styles.css): the cart is a sheet over the menu there. */}
                <button
                  type="button"
                  className="btn sm cart-back"
                  onClick={() => setCartOpen(false)}
                >
                  ← Menu
                </button>
                <div>
                  <h2>{current.number}</h2>
                  <div className="small muted">
                    {current.tableCode
                      ? `Table ${current.tableCode}`
                      : current.roomNumber
                        ? `Room ${current.roomNumber} · ${current.guestName}`
                        : current.type.replace('_', ' ')}{' '}
                    · {time(current.openedAt)}
                  </div>
                </div>
                <div className="row gap-6">
                  {current.lines.length > 0 ? (
                    <ThermalPrintButton
                      data={ticketKot(current, session?.property?.name ?? 'Kitchen')}
                      kind="kot"
                      className="btn sm"
                      label="KOT"
                    />
                  ) : null}
                  {can('pos:void') ? (
                    <button
                      className="btn danger icon sm"
                      title="Void ticket"
                      onClick={() => setVoidTicket(true)}
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="ticket-lines">
                {current.lines.length === 0 ? (
                  <Empty message="Tap items to add them" />
                ) : (
                  current.lines.map((line) => (
                    <div className="tline" key={line.id}>
                      <div className="tl-name">
                        {line.name}
                        {/* What the kitchen has done with this one. The
                            counter is asked "where is my food" per dish, not
                            per ticket, so the answer belongs per line. */}
                        <KitchenState line={line} />
                      </div>
                      <div className="tl-amt">{money(line.amount)}</div>
                      <div className="tl-meta">
                        <div className="stepper">
                          <button
                            onClick={() =>
                              addLine.mutate({ menuItemId: line.menuItemId, quantity: -1 })
                            }
                            disabled={addLine.isPending}
                          >
                            −
                          </button>
                          <span className="q">{line.quantity}</span>
                          <button
                            onClick={() =>
                              addLine.mutate({ menuItemId: line.menuItemId, quantity: 1 })
                            }
                            disabled={addLine.isPending}
                          >
                            +
                          </button>
                        </div>
                        <span className="xs muted">
                          @ {money0(line.unitPrice)} · GST {line.gstRate}%
                          {line.discountPct > 0
                            ? ` · less ${line.discountPct}% (${money(line.discount)})`
                            : ''}
                        </span>
                        {/*
                         * A queued item comes straight off - nobody has
                         * touched it. Once the kitchen has started, removing
                         * it means food already being cooked leaves the bill,
                         * so the counter is asked to confirm rather than
                         * blocked: the till cannot know whether the guest
                         * changed their mind or the kitchen misheard.
                         */}
                        <button
                          className="btn ghost sm"
                          style={{ marginLeft: 'auto' }}
                          onClick={() =>
                            line.kdsState === 'QUEUED'
                              ? removeLine.mutate(line.id)
                              : setRemoving(line)
                          }
                          disabled={removeLine.isPending || line.kdsState === 'SERVED'}
                          title={
                            line.kdsState === 'SERVED'
                              ? 'A served item cannot be removed - void the ticket to take it off'
                              : undefined
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="totals">
                {current.totals.itemDiscount > 0 ? (
                  <div className="t-row">
                    <span>Menu discounts</span>
                    <span className="num">− {money(current.totals.itemDiscount)}</span>
                  </div>
                ) : null}

                {/* Named, so a cashier can tell a guest which offer took what. */}
                {current.offers.map((offer) => (
                  <div className="t-row offer" key={offer.id}>
                    <span>
                      {offer.name}
                      {offer.times > 1 ? <span className="xs muted"> ×{offer.times}</span> : null}
                    </span>
                    <span className="num">− {money(offer.discount)}</span>
                  </div>
                ))}
                <div className="t-row">
                  <span>Taxable value</span>
                  <span className="num">{money(foodNet)}</span>
                </div>
                <div className="t-row row between" style={{ alignItems: 'center' }}>
                  <span>Bill discount %</span>
                  <span className="row gap-6" style={{ alignItems: 'center' }}>
                    {discount > 0 ? <span className="xs muted">− {money(discount)}</span> : null}
                    <input
                      className="input"
                      type="number"
                      min={0}
                      max={100}
                      step={5}
                      value={discountPct}
                      disabled={!can('pos:discount')}
                      title={can('pos:discount') ? '' : 'Your role cannot apply discounts'}
                      onChange={(e) =>
                        setDiscountPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
                      }
                      style={{ width: 72, height: 27, textAlign: 'right' }}
                    />
                  </span>
                </div>

                {/* Packing on a takeaway, packing and a fee on a delivery: the
                    counter sees them before billing, not after. */}
                {current.charges.map((charge) => (
                  <div className="t-row" key={charge.key}>
                    <span>{charge.label}</span>
                    <span className="num">{money(charge.amount)}</span>
                  </div>
                ))}
                <div className="t-row">
                  <span>GST</span>
                  <span className="num">{money(estimate.tax)}</span>
                </div>
                <div className="t-row">
                  <span>Round off</span>
                  <span className="num">{money(estimate.roundOff)}</span>
                </div>
                <div className="t-row grand">
                  <span>Total</span>
                  <span className="num">{money(estimate.total)}</span>
                </div>

                <div className="pay-modes" style={{ marginTop: 11 }}>
                  {PAYMENT_MODES.map((option) => (
                    <button
                      key={option}
                      className={mode === option ? 'active' : ''}
                      disabled={option === 'ROOM' && !current.stayId}
                      onClick={() => setMode(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                <button
                  className="btn primary lg block"
                  data-tour="pos-bill"
                  style={{ marginTop: 10 }}
                  disabled={current.lines.length === 0 || bill.isPending}
                  onClick={() => bill.mutate()}
                >
                  {bill.isPending ? 'Billing…' : `Bill ${money0(estimate.total)}`}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Phone only: keeps the total and the way to the cart in reach without
            the cart having to sit below the whole menu. */}
        {current ? (
          <button
            type="button"
            className="cart-bar"
            onClick={() => setCartOpen(true)}
            aria-label={`View cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}, total ${money0(estimate.total)}`}
          >
            <span className="cart-bar-count" aria-hidden="true">
              {itemCount}
            </span>
            <span className="cart-bar-label" aria-hidden="true">
              {itemCount === 0 ? 'Tap items to add' : 'View cart'}
            </span>
            <span className="cart-bar-total" aria-hidden="true">
              {money0(estimate.total)} ›
            </span>
          </button>
        ) : null}
      </div>

      {tableSetup ? <TableSetup onClose={() => setTableSetup(false)} /> : null}
      {qrCodes ? <TableQrCodes onClose={() => setQrCodes(false)} /> : null}
      {recordingExpense ? (
        <RecordExpenseModal onClose={() => setRecordingExpense(false)} />
      ) : null}

      {newTicket && source ? (
        <NewTicketModal
          source={source}
          onClose={() => setNewTicket(false)}
          onCreated={(id) => setActiveId(id)}
        />
      ) : null}

      {voidTicket && current ? (
        <VoidTicketDialog
          number={current.number}
          busy={voidMutation.isPending}
          onCancel={() => setVoidTicket(false)}
          onConfirm={(reason) => voidMutation.mutate(reason)}
        />
      ) : null}

      {removing ? (
        <RemoveStartedLineDialog
          line={removing}
          busy={removeLine.isPending}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            removeLine.mutate(removing.id);
            setRemoving(null);
          }}
        />
      ) : null}

      {receipt ? (
        <Modal
          title={`Invoice ${receipt.number}`}
          size="wide"
          onClose={() => setReceipt(null)}
          footer={
            <>
              <PaperWidthPicker />
              <button className="btn" onClick={() => setReceipt(null)}>
                Close
              </button>
              <button className="btn" onClick={() => window.print()}>
                A4 invoice
              </button>
              <ThermalPrintButton
                data={invoiceReceipt(receipt)}
                className="btn primary"
                label="Print receipt"
              />
            </>
          }
        >
          <InvoiceDoc invoice={receipt} />
        </Modal>
      ) : null}
    </div>
  );
}

/*
 * Asked before removing a line the kitchen has already started.
 *
 * The counter is not stopped, because the till has no way of knowing which
 * situation this is: a guest who changed their mind while the pan was on, or
 * an item rung up on the wrong table. Both are ordinary, and both are fixed by
 * removing the line. What the counter can be told is the part that is easy to
 * miss on a busy screen - that food is being cooked and this takes it off the
 * bill - so the decision is made knowingly rather than by reflex.
 *
 * Cancel is the default action so a stray tap costs nothing.
 */
function RemoveStartedLineDialog({
  line,
  busy,
  onCancel,
  onConfirm,
}: {
  line: TicketLineDto;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const started = line.kdsState === 'READY' ? 'has already cooked' : 'has started cooking';

  return (
    <Modal
      title="Remove an item the kitchen has started?"
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel} disabled={busy}>
            Keep it on the ticket
          </button>
          <button className="btn danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Removing…' : 'Remove anyway'}
          </button>
        </>
      }
    >
      <p>
        The kitchen {started} <strong>{line.quantity}</strong> × {line.name}.
      </p>
      <p className="muted">
        Removing it now takes {money(line.amount)} off this bill, but the food has been made. Let
        the kitchen know so it is not sent out, and record the waste.
      </p>
    </Modal>
  );
}

function VoidTicketDialog({
  number,
  busy,
  onCancel,
  onConfirm,
}: {
  number: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  /*
   * Picked from a list rather than typed.
   *
   * A cashier voiding a ticket has a guest standing in front of them, and what
   * gets typed in that moment is whatever is quickest - which leaves an owner
   * reading the audit trail a month later none the wiser. The list is the
   * reasons a restaurant actually voids for; anything genuinely different can
   * still be written out.
   */
  const [choice, setChoice] = useState<string>(VOID_REASONS[0]);
  const [other, setOther] = useState('');
  const reason = choice === OTHER ? other.trim() : choice;

  return (
    <Modal
      title={`Void ${number}`}
      size="narrow"
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel}>
            Keep ticket
          </button>
          <button
            className="btn danger"
            disabled={!reason || busy}
            onClick={() => onConfirm(reason)}
          >
            Void ticket
          </button>
        </>
      }
    >
      <p className="dim" style={{ marginBottom: 14 }}>
        Nothing will be billed. The reason is written to the audit log.
      </p>
      <div className="col gap-16">
        <Select
          label="Reason"
          autoFocus
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
        >
          {VOID_REASONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
          <option value={OTHER}>Something else…</option>
        </Select>

        {choice === OTHER ? (
          <TextInput
            label="What happened?"
            autoFocus
            value={other}
            onChange={(e) => setOther(e.target.value)}
            placeholder="Keyed on the wrong table"
          />
        ) : null}
      </div>
    </Modal>
  );
}

function NewTicketModal({
  source,
  onClose,
  onCreated,
}: {
  source: PosDataSource;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const { fail } = useToast();
  // Takeaway needs no table or stay pick, so a cashier who wants one can hit
  // "Open ticket" right away - dine-in is still one click away when needed.
  const [type, setType] = useState('TAKEAWAY');
  const [tableId, setTableId] = useState('');
  const [stayId, setStayId] = useState('');
  const [covers, setCovers] = useState(2);

  const tables = useQuery<TableDto[]>({
    queryKey: ['tables'],
    queryFn: () => source.listTables(),
  });
  const stays = useQuery<StayDto[]>({
    queryKey: ['stays', 'IN_HOUSE'],
    queryFn: () => source.listStays(),
  });

  const create = useMutation({
    mutationFn: () =>
      source.createTicket({
        type,
        covers,
        ...(type === 'DINE_IN' ? { tableId } : {}),
        ...(type === 'ROOM_SERVICE' ? { stayId } : {}),
      }),
    onSuccess: (ticket) => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      onCreated(ticket.id);
      onClose();
    },
    onError: fail,
  });

  const free = (tables.data ?? []).filter((table) => !table.isOccupied);

  return (
    <Modal
      title="New ticket"
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={
              create.isPending ||
              (type === 'DINE_IN' && !tableId) ||
              (type === 'ROOM_SERVICE' && !stayId)
            }
            onClick={() => create.mutate()}
          >
            Open ticket
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <Select label="Service type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="DINE_IN">Dine-in</option>
          <option value="TAKEAWAY">Takeaway</option>
          <option value="DELIVERY">Delivery</option>
          <option value="ROOM_SERVICE" disabled={(stays.data?.length ?? 0) === 0}>
            Room service
          </option>
        </Select>

        {type === 'DINE_IN' ? (
          <Select
            label="Table"
            value={tableId}
            onChange={(e) => setTableId(e.target.value)}
            hint={free.length === 0 ? 'Every table has an open ticket' : undefined}
          >
            <option value="">Select a table…</option>
            {free.map((table) => (
              <option key={table.id} value={table.id}>
                {table.code} · {table.seats} seats · {table.section}
              </option>
            ))}
          </Select>
        ) : null}

        {type === 'ROOM_SERVICE' ? (
          <Select label="In-house guest" value={stayId} onChange={(e) => setStayId(e.target.value)}>
            <option value="">Select a room…</option>
            {(stays.data ?? []).map((stay) => (
              <option key={stay.id} value={stay.id}>
                Room {stay.roomNumber} · {stay.guestName}
              </option>
            ))}
          </Select>
        ) : null}

        <TextInput
          label="Covers"
          type="number"
          min={1}
          max={50}
          value={covers}
          onChange={(e) => setCovers(Number(e.target.value) || 1)}
        />
      </div>
    </Modal>
  );
}

/**
 * What the kitchen has done with one line, on the counter's own ticket.
 *
 * The state is already on every line - the kitchen display writes it as items
 * are started and passed - it simply was not shown anywhere the person taking
 * the money could see it. A guest asking after their food asks about a dish,
 * so the answer is per line rather than per ticket.
 *
 * Anything not yet on the table also carries how long it has been waiting.
 * "Cooking" on its own is reassuring at four minutes and alarming at twenty,
 * and the counter is the one being asked which it is.
 */
function KitchenState({ line }: { line: TicketLineDto }) {
  const LABELS: Record<string, { label: string; tone: string }> = {
    QUEUED: { label: 'In queue', tone: '' },
    PREPARING: { label: 'Cooking', tone: 'warn' },
    READY: { label: 'Ready', tone: 'ok' },
    SERVED: { label: 'Served', tone: 'muted' },
  };

  const state = LABELS[line.kdsState] ?? { label: line.kdsState, tone: '' };
  const waiting = line.kdsState === 'QUEUED' || line.kdsState === 'PREPARING';
  const minutes = waiting ? Math.floor((Date.now() - new Date(line.firedAt).getTime()) / 60_000) : 0;

  return (
    <span className="kds-state">
      <span className={`chip ${state.tone}`}>{state.label}</span>
      {waiting && minutes > 0 ? <span className="xs muted">{minutes}m</span> : null}
    </span>
  );
}
